import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Activity, Users, TrendingUp, AlertCircle, Clock, BarChart3, 
  RefreshCw, Download, ArrowUp, ArrowDown 
} from 'lucide-react';
import { adminApi } from '../utils/api';
import { toast } from 'sonner';

export const Analytics = ({ adminSecret }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [requests, setRequests] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [requestPeriod, setRequestPeriod] = useState('day');
  
  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line
  }, [adminSecret]);
  
  useEffect(() => {
    if (overview) {
      fetchRequestTrends();
    }
    // eslint-disable-next-line
  }, [requestPeriod, overview]);
  
  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, endpointsRes] = await Promise.all([
        adminApi.getAnalyticsOverview(adminSecret),
        adminApi.getAnalyticsEndpoints(adminSecret)
      ]);
      
      setOverview(overviewRes.data);
      setEndpoints(endpointsRes.data.endpoints);
      
      // Fetch request trends
      await fetchRequestTrends();
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRequestTrends = async () => {
    try {
      const limit = requestPeriod === 'hour' ? 24 : requestPeriod === 'day' ? 30 : 12;
      const response = await adminApi.getAnalyticsRequests(adminSecret, requestPeriod, limit);
      setRequests(response.data.data);
    } catch (error) {
      console.error('Failed to load request trends:', error);
    }
  };
  
  const handleExport = async () => {
    try {
      const response = await adminApi.exportAnalytics(adminSecret);
      const dataStr = JSON.stringify(response.data.logs, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Analytics exported successfully');
    } catch (error) {
      toast.error('Failed to export analytics');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-indicator">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  if (!overview) {
    return <div>No data available</div>;
  }
  
  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-2">Monitor your API usage and performance</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={fetchAnalytics}
            data-testid="refresh-analytics-button"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button
            variant="outline"
            onClick={handleExport}
            data-testid="export-analytics-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>
      
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Users */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="total-users">
                  {overview.users.total}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {overview.users.active} active
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Total Requests */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Requests</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="total-requests">
                  {overview.requests.total.toLocaleString()}
                </p>
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <ArrowUp className="h-3 w-3" />
                  {overview.requests.today} today
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Activity className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Error Rate */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Error Rate</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="error-rate">
                  {overview.errors.error_rate}%
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {overview.errors.total} errors total
                </p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Avg Response Time */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Response</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="avg-response-time">
                  {overview.performance.avg_response_time_ms}
                  <span className="text-lg">ms</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Performance metric
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Request Trends Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Request Trends
            </CardTitle>
            <Select value={requestPeriod} onValueChange={setRequestPeriod}>
              <SelectTrigger className="w-[140px]" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hour">Last 24 Hours</SelectItem>
                <SelectItem value="day">Last 30 Days</SelectItem>
                <SelectItem value="week">Last 12 Weeks</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {requests.length > 0 ? (
            <div className="space-y-4" data-testid="request-trends-chart">
              {/* Simple Bar Chart */}
              <div className="space-y-2">
                {requests.slice(0, 10).map((req, index) => {
                  const maxRequests = Math.max(...requests.map(r => r.total_requests));
                  const width = (req.total_requests / maxRequests) * 100;
                  
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{req.period}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-green-600">{req.successful_requests} ✓</span>
                          <span className="text-red-600">{req.failed_requests} ✗</span>
                          <span className="font-semibold">{req.total_requests}</span>
                        </div>
                      </div>
                      <div className="h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-end px-3 text-white text-sm font-medium transition-all duration-300"
                          style={{ width: `${width}%` }}
                        >
                          {req.total_requests > 0 && `${req.avg_response_time_ms}ms`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Legend */}
              <div className="flex items-center gap-4 text-xs text-gray-600 pt-2 border-t">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span>Request Volume</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-green-600">✓</span>
                  <span>Success</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-red-600">✗</span>
                  <span>Failed</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No request data available</p>
          )}
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Endpoints */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Top Endpoints
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="top-endpoints">
              {endpoints.slice(0, 10).map((endpoint, index) => (
                <div key={index} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {endpoint.endpoint}
                    </code>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs ${endpoint.success_rate >= 95 ? 'text-green-600' : endpoint.success_rate >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {endpoint.success_rate}%
                      </span>
                      <span className="text-xs text-gray-600">
                        {endpoint.total_requests} req
                      </span>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${endpoint.success_rate >= 95 ? 'bg-green-500' : endpoint.success_rate >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${endpoint.success_rate}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Avg: {endpoint.avg_response_time_ms}ms</span>
                    <span>Min: {endpoint.min_response_time_ms}ms | Max: {endpoint.max_response_time_ms}ms</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Top Users */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Most Active Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="top-users">
              {overview.top_users && overview.top_users.length > 0 ? (
                overview.top_users.map((user, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{user.name}</p>
                      <p className="text-xs text-gray-600">{user.email}</p>
                      <code className="text-xs text-gray-500 mt-1 block">
                        {user.api_key.substring(0, 20)}...
                      </code>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">
                        {user.request_count}
                      </p>
                      <p className="text-xs text-gray-500">requests</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-8">No user data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Active Users Alert */}
      {overview.users.active_last_24h > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-semibold text-blue-900">
                  {overview.users.active_last_24h} users active in the last 24 hours
                </p>
                <p className="text-sm text-blue-700">
                  Your API is being actively used. Monitor performance and errors regularly.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
